// src/pages/procurement/SupplierStatementPage.jsx
//
// Full supplier ledger — opening balance, every transaction (purchase orders, supplier
// payments, debit notes) within a selectable date range, and a closing balance. Reached
// from PayablesPage.jsx's action button. Cloned from
// src/pages/offlineSales/CustomerStatementPage.jsx. Backend: payables.service.js's
// getSupplierStatement (rettzy-backend .../modules/procurement/services/payables.service.js).
//
// Transaction "type" vocabulary differs from the sell-side statement: purchase_order /
// supplier_payment / debit_note (not invoice / payment / credit_note / return).
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FileText, ArrowLeft, Printer, ShoppingCart, FileMinus, Wallet, Calendar, TrendingUp } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';
import { PageHeader, DataTable } from '../../components/common';
import DateRangePicker from '../../components/ui/DateRangePicker/DateRangePicker';
import { getSupplierStatement } from '../../services/api/procurement/payables';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { PURCHASE_ORDER_BASE_PATH } from '../../utils/procurementNavigation';
import SupplierStatementPrintModal from './components/SupplierStatementPrintModal';
import styles from './SupplierStatementPage.module.css';

const TYPE_LABELS = {
  purchase_order: 'Purchase Order',
  supplier_payment: 'Payment',
  debit_note: 'Debit Note',
};

const TYPE_ICONS = {
  purchase_order: ShoppingCart,
  supplier_payment: Wallet,
  debit_note: FileMinus,
};

const SupplierStatementPage = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { error: showError } = useAlert();

  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [showPrint, setShowPrint] = useState(false);

  const loadStatement = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getSupplierStatement(supplierId, {
        dateFrom: dateRange.from || undefined,
        dateTo: dateRange.to || undefined,
      });
      setStatement(result);
    } catch (err) {
      showError(err?.response?.data?.message || t('procurement.failedToLoadSupplierStatement'));
    } finally {
      setLoading(false);
    }
  }, [supplierId, dateRange, showError, t]);

  useEffect(() => { loadStatement(); }, [loadStatement]);

  const columns = [
    {
      key: 'date',
      header: t('procurement.date'),
      render: (row) => <span className={styles.typeCell}><Calendar size={14} />{formatDate(row.date)}</span>,
    },
    {
      key: 'type',
      header: t('procurement.type'),
      render: (row) => {
        const TypeIcon = TYPE_ICONS[row.type] || FileText;
        return (
          <span className={styles.typeCell}>
            <TypeIcon size={14} />
            {t(`procurement.transactionType_${row.type}`) || TYPE_LABELS[row.type] || row.type}
          </span>
        );
      },
    },
    {
      key: 'reference',
      header: t('procurement.reference'),
      render: (row) => {
        let ref = row.reference;
        if (row.debitNoteId) {
          ref = <Link className={styles.docLink} to={`/procurement/debit-notes/${row.debitNoteId}`}>{row.reference}</Link>;
        } else if (row.purchaseOrderId) {
          ref = <Link className={styles.docLink} to={`${PURCHASE_ORDER_BASE_PATH}/${row.purchaseOrderId}`}>{row.reference}</Link>;
        }
        if (row.type === 'supplier_payment' && row.method) {
          return (
            <div>
              {ref}
              <div className={styles.refMeta}>{row.method}</div>
            </div>
          );
        }
        return ref;
      },
    },
    {
      key: 'debit',
      header: t('procurement.debit'),
      align: 'right',
      render: (row) => ((row.debit > 0 || row.type === 'purchase_order')
        ? <span className={`${styles.amountCell} ${styles.debitAmount}`}>{formatCurrency(row.debit)}</span>
        : <span className={styles.mutedDash}>—</span>),
    },
    {
      key: 'credit',
      header: t('procurement.credit'),
      align: 'right',
      render: (row) => (row.credit > 0
        ? <span className={`${styles.amountCell} ${styles.creditAmount}`}>{formatCurrency(row.credit)}</span>
        : <span className={styles.mutedDash}>—</span>),
    },
    {
      key: 'runningBalance',
      header: t('procurement.balance'),
      align: 'right',
      render: (row) => <span className={`${styles.amountCell} ${styles.balanceAmount}`}>{formatCurrency(row.runningBalance)}</span>,
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('procurement.supplierStatement')}
        description={statement?.supplier?.name}
        icon={FileText}
        actions={
          <div className={styles.headerActions}>
            <button className={styles.secondaryBtn} onClick={() => navigate('/procurement/payables')}>
              <ArrowLeft size={16} /> {t('common.back')}
            </button>
            {statement && (
              <button className={styles.secondaryBtn} onClick={() => setShowPrint(true)}>
                <Printer size={15} /> {t('common.printReceipt', 'Print Receipt')}
              </button>
            )}
          </div>
        }
      />

      <div className={styles.filterRow}>
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          placeholder={t('procurement.selectDateRange')}
        />
      </div>

      {statement && (
        <div className={styles.balanceCards}>
          <div className={styles.balanceCard}>
            <div className={styles.balanceCardLabel}><Wallet size={14} />{t('procurement.openingBalance')}</div>
            <div className={styles.balanceCardValue}>{formatCurrency(statement.openingBalance)}</div>
          </div>
          <div className={styles.balanceCard}>
            <div className={styles.balanceCardLabel}><TrendingUp size={14} />{t('procurement.closingBalance')}</div>
            <div className={`${styles.balanceCardValue} ${styles.balanceCardValueClosing}`}>{formatCurrency(statement.closingBalance)}</div>
          </div>
        </div>
      )}

      <p className={styles.orderHint}>{t('procurement.statementOrderHint')}</p>

      <DataTable
        data={statement?.transactions || []}
        columns={columns}
        loading={loading}
        emptyMessage={t('procurement.noTransactionsFound')}
        emptyDescription={t('procurement.noTransactionsFoundDescription')}
      />

      {showPrint && statement && (
        <SupplierStatementPrintModal statement={statement} onClose={() => setShowPrint(false)} />
      )}
    </div>
  );
};

export default SupplierStatementPage;
